import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { OtherFee } from '../../../types/financial';

interface OtherFeeRecord {
  id: string;
  registrationId: string;
  invoiceNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  fee: OtherFee;
  createdAt: Date;
}

const OtherFeesReport: React.FC = () => {
  const [otherFees, setOtherFees] = useState<OtherFeeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOtherFees = async () => {
      setLoading(true);
      setError('');
      try {
        // Get all registrations
        const registrationsRef = collection(db, 'Registrations');
        const registrationsSnapshot = await getDocs(registrationsRef);
        
        const allOtherFees: OtherFeeRecord[] = [];
        
        registrationsSnapshot.forEach(doc => {
          const data = doc.data();
          
          // Skip pending registrations
          const status = data.status?.toLowerCase();
          if (status === 'pending') {
            return; // Skip this record
          }
          
          // Extract name from different possible structures
          const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || data.firstName || 'Unknown';
          const lastName = nameParts.slice(1).join(' ') || data.lastName || 'Unknown';
          const email = data.registrationInfo?.email || data.email || 'Unknown';
          const invoiceNumber = data.financialRecord?.invoiceNumber || data.invoiceNumber || data.invoice?.invoiceNumber || 'N/A';
          
          // Process custom other fees from otherFees array
          const otherFeesArray = data.otherFees?.otherFees || data.financial?.otherFees?.otherFees || [];
          if (Array.isArray(otherFeesArray)) {
            otherFeesArray.forEach((fee: OtherFee) => {
              allOtherFees.push({
                id: doc.id,
                registrationId: doc.id,
                invoiceNumber,
                firstName,
                lastName,
                email,
                fee: fee,
                createdAt: data.createdAt?.toDate() || new Date()
              });
            });
          }
          
          // Check for customFee (admin-added "Other Fees" with note)
          const customFee = data.financial?.otherFees?.customFee;
          if (customFee && typeof customFee.amount === 'number' && customFee.amount > 0) {
            allOtherFees.push({
              id: doc.id,
              registrationId: doc.id,
              invoiceNumber,
              firstName,
              lastName,
              email,
              fee: {
                id: 'customFee',
                amount: customFee.amount,
                note: customFee.note || 'Other Fee'
              },
              createdAt: data.createdAt?.toDate() || new Date()
            });
          }
          
          // Check for override amounts
          const otherFeesOverride = data.otherFees?.override || data.financial?.otherFees?.override || data.financial?.overrides?.otherFees;
          if (otherFeesOverride && typeof otherFeesOverride.amount === 'number' && otherFeesOverride.amount !== 0) {
            allOtherFees.push({
              id: doc.id,
              registrationId: doc.id,
              invoiceNumber,
              firstName,
              lastName,
              email,
              fee: {
                id: 'override',
                amount: otherFeesOverride.amount,
                note: otherFeesOverride.note || otherFeesOverride.notes || 'Override Amount'
              },
              createdAt: data.createdAt?.toDate() || new Date()
            });
          }
        });
        
        
        
        // Sort by creation date (newest first)
        allOtherFees.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        setOtherFees(allOtherFees);
        
        
      } catch (err) {
        console.error('Error fetching other fees:', err);
        setError('Failed to fetch other fees data');
      } finally {
        setLoading(false);
      }
    };

    fetchOtherFees();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const totalAmount = otherFees.reduce((sum, record) => sum + record.fee.amount, 0);

  if (loading) {
    return <div>Loading other fees data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="other-fees-report">
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <h1 style={{ margin: 0, textAlign: 'center' }}>Other Fees Report (Custom Fees + Overrides)</h1>
        <button 
          onClick={handlePrint}
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
        >
          🖨️ Print Report
        </button>
      </div>
      
      <p style={{ textAlign: 'center', marginBottom: '32px' }}>
        Total Other Fees (Custom Fees + Overrides): ${totalAmount.toFixed(2)} | Total Records: {otherFees.length}
      </p>
      
      

      {otherFees.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No custom other fees found.</p>
        </div>
      ) : (
        <div className="other-fees-table-container">
          <table className="other-fees-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Invoice #</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Camper Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Fee Note</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Date Added</th>
              </tr>
            </thead>
            <tbody>
              {otherFees.map((record, index) => (
                <tr key={`${record.id}-${record.fee.id}`} style={{ 
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {record.invoiceNumber}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {record.firstName} {record.lastName}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {record.email}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {record.fee.note || 'No note provided'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>
                    ${record.fee.amount.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    {record.createdAt.toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ padding: '12px', textAlign: 'right', borderTop: '2px solid #dee2e6' }}>
                  Total:
                </td>
                <td style={{ padding: '12px', textAlign: 'right', borderTop: '2px solid #dee2e6' }}>
                  ${totalAmount.toFixed(2)}
                </td>
                <td style={{ padding: '12px', borderTop: '2px solid #dee2e6' }}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default OtherFeesReport;